import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { useNavigate } from "react-router-dom";
import {
  Wallet as WalletIcon,
  Copy,
  ExternalLink,
  Coins,
  TrendingUp,
  ArrowUpRight,
  Shield,
  QrCode,
  RefreshCw,
  ShoppingCart,
  Trophy,
  BarChart3,
} from "lucide-react";
import { toast } from "sonner";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ["#0F2744", "#3B82F6", "#10B981"];

export default function WalletPage() {
  const [wallet, setWallet] = useState(null);
  const [tracker, setTracker] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [walletRes, trackerRes] = await Promise.all([
        axios.get(`${API}/wallet`),
        axios.get(`${API}/portfolio/tracker`),
      ]);
      setWallet(walletRes.data);
      setTracker(trackerRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fmt = (value) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);

  const copyAddress = () => {
    if (wallet?.address) {
      navigator.clipboard.writeText(wallet.address);
      toast.success("Wallet address copied!");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#3B82F6] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Build bar chart data for projected growth per holding
  const growthChartData = tracker?.holdings?.map((h) => ({
    name: h.token_symbol,
    current: h.current_value,
    "6 mo": h.projected_6m,
    "12 mo": h.projected_12m,
    "24 mo": h.projected_24m,
  })) || [];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="wallet-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Blockchain Wallet</h1>
          <p className="text-slate-500 mt-1">Manage your tokenized assets and track performance</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} data-testid="refresh-wallet-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button
            className="bg-[#0F2744] hover:bg-[#1E3A5F] text-white"
            onClick={() => navigate("/buy-tokens")}
            data-testid="buy-more-btn"
          >
            <ShoppingCart className="w-4 h-4 mr-2" /> Buy Tokens
          </Button>
        </div>
      </div>

      {/* Wallet Address Card */}
      <Card className="border-slate-200 bg-gradient-to-br from-[#0F2744] to-[#1E3A5F] text-white" data-testid="wallet-card">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <p className="text-white/60 text-sm mb-1">Wallet Address</p>
              <div className="flex items-center gap-3">
                <code className="text-lg font-mono">
                  {wallet?.address?.slice(0, 12)}...{wallet?.address?.slice(-8)}
                </code>
                <button onClick={copyAddress} className="p-2 hover:bg-white/10 rounded-lg transition-colors" data-testid="copy-address-btn">
                  <Copy className="w-4 h-4" />
                </button>
                <button className="p-2 hover:bg-white/10 rounded-lg transition-colors" data-testid="view-explorer-btn">
                  <ExternalLink className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white/10 rounded-xl flex items-center justify-center">
                <QrCode className="w-8 h-8" />
              </div>
              <div className="text-right">
                <p className="text-white/60 text-sm">Portfolio Value</p>
                <p className="text-3xl font-bold">{fmt(tracker?.total_current_value || 0)}</p>
              </div>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-2 text-white/60 text-sm">
              <Shield className="w-4 h-4" />
              <span>Secured by Smart Contract</span>
            </div>
            <span className="text-xs text-white/40">Network: Ethereum</span>
          </div>
        </CardContent>
      </Card>

      {/* Performance Stats */}
      {tracker && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-slate-200" data-testid="stat-total-invested">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-10 h-10 bg-[#E8F4FD] rounded-xl flex items-center justify-center">
                <Coins className="w-5 h-5 text-[#3B82F6]" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Total Invested</p>
                <p className="text-xl font-bold text-slate-900">{fmt(tracker.total_invested)}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200" data-testid="stat-projected-gain">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Projected Gain (24mo)</p>
                <p className="text-xl font-bold text-emerald-600">+{fmt(tracker.total_gain_24m)}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200" data-testid="stat-growth-pct">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Growth Rate (24mo)</p>
                <p className="text-xl font-bold text-emerald-600">+{tracker.total_gain_pct}%</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200" data-testid="stat-best-performer">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center">
                <Trophy className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Best Performer</p>
                <p className="text-xl font-bold text-slate-900">{tracker.best_performer?.token_symbol}</p>
                <p className="text-[10px] text-emerald-600 font-medium">+{tracker.best_performer?.growth_24m}%</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Growth Comparison Chart */}
      {tracker && growthChartData.length > 0 && (
        <Card className="border-slate-200" data-testid="growth-comparison-chart">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base font-semibold text-slate-900">
              Holdings Value Projection
            </CardTitle>
            {tracker.is_demo && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
                Demo Portfolio
              </span>
            )}
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={growthChartData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748b" }} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip
                  formatter={(v) => fmt(v)}
                  contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }}
                />
                <Bar dataKey="current" name="Current" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                <Bar dataKey="6 mo" name="6 Months" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="12 mo" name="12 Months" fill="#0F2744" radius={[4, 4, 0, 0]} />
                <Bar dataKey="24 mo" name="24 Months" fill="#10B981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Detailed Holdings */}
      <Card className="border-slate-200" data-testid="holdings-detail">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold text-slate-900">Token Holdings</CardTitle>
          <Button variant="ghost" className="text-[#3B82F6]" onClick={() => navigate("/buy-tokens")} data-testid="buy-more-tokens-btn">
            Buy More <ArrowUpRight className="w-4 h-4 ml-1" />
          </Button>
        </CardHeader>
        <CardContent>
          {tracker && tracker.holdings.length > 0 ? (
            <div className="space-y-4">
              {tracker.holdings.map((h, i) => (
                <div
                  key={h.project_id}
                  className="p-4 border border-slate-200 rounded-xl hover:border-blue-300 transition-all cursor-pointer"
                  onClick={() => navigate(`/projects/${h.project_id}`)}
                  data-testid={`holding-detail-${h.token_symbol}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <img src={h.image_url} alt={h.project_name} className="w-12 h-12 rounded-xl object-cover" />
                      <div>
                        <h4 className="font-semibold text-slate-900">{h.project_name}</h4>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs font-mono text-slate-500">{h.token_symbol}</span>
                          <span className="text-xs text-slate-400">|</span>
                          <span className="text-xs text-slate-500">{h.industry}</span>
                          <span className={`text-xs font-medium ${h.score >= 800 ? "text-emerald-600" : h.score >= 500 ? "text-blue-600" : "text-amber-600"}`}>
                            Score: {h.score}/1000
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-slate-900">{h.tokens} tokens</p>
                      <p className="text-sm text-slate-500">{h.allocation_pct}% of portfolio</p>
                    </div>
                  </div>

                  {/* Value Projections Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div className="bg-slate-50 rounded-lg p-2.5">
                      <p className="text-[10px] text-slate-500 uppercase tracking-wide">Invested</p>
                      <p className="font-bold text-slate-900">{fmt(h.invested)}</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-2.5">
                      <p className="text-[10px] text-slate-500 uppercase tracking-wide">Current</p>
                      <p className="font-bold text-slate-900">{fmt(h.current_value)}</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-2.5">
                      <p className="text-[10px] text-blue-600 uppercase tracking-wide">6 Months</p>
                      <p className="font-bold text-blue-700">{fmt(h.projected_6m)}</p>
                      <p className="text-[10px] text-emerald-600 font-medium">+{h.growth_6m}%</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-2.5">
                      <p className="text-[10px] text-blue-600 uppercase tracking-wide">12 Months</p>
                      <p className="font-bold text-blue-700">{fmt(h.projected_12m)}</p>
                      <p className="text-[10px] text-emerald-600 font-medium">+{h.growth_12m}%</p>
                    </div>
                    <div className="bg-emerald-50 rounded-lg p-2.5">
                      <p className="text-[10px] text-emerald-600 uppercase tracking-wide">24 Months</p>
                      <p className="font-bold text-emerald-700">{fmt(h.projected_24m)}</p>
                      <p className="text-[10px] text-emerald-600 font-medium">+{h.growth_24m}%</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <WalletIcon className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No tokens in your wallet yet.</p>
              <Button className="mt-4 bg-[#0F2744] hover:bg-[#1E3A5F]" onClick={() => navigate("/buy-tokens")} data-testid="browse-cta">
                Browse Tokens to Invest
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Security Notice */}
      <Card className="border-blue-200 bg-blue-50" data-testid="security-notice">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">Wallet Security</p>
              <p className="text-sm text-blue-700 mt-1">
                Your tokens are secured by blockchain smart contracts. All transactions are verified
                and recorded on the distributed ledger for complete transparency and security.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
